import {
  getOrderCakeInstructionForRow,
  getOrderSpecialRequestText,
} from './orderTotals';
import { formatDate } from './formatTime';
import {
  buildColorLabelByCode,
  ColorLabelSource,
  getColorDisplayLabel,
} from './colorDisplay';

export interface OrderBundleItemForPDF {
  id: string;
  labelEn: string;
  labelAr?: string;
  qty: number;
  unitPrice: number;
  subTotal: number;
  previewUrl?: string | null;
  /** Preloaded data URL for PDF (preferred over previewUrl) */
  previewDataUrl?: string | null;
  components?: Array<{
    id: string;
    componentType: string;
    labelEn: string;
    labelAr?: string;
    qty: number;
    unitPrice: number;
    subTotal: number;
    previewUrl?: string | null;
  }>;
  variantSelections?: Array<{
    id: string;
    labelEn: string;
    labelAr?: string;
    qty: number;
    price: number;
    subTotal: number;
    previewUrl?: string | null;
  }>;
}

export interface OrderDetailsForPDF {
  order: {
    id: string;
    orderNumber: string;
    orderDate: string;
    status: string;
    totalAmount: number;
    promoCodeDiscountAmount: number;
    walletCreditUsed: number;
    specialRequest?: string | null;
    cardMessage: {
      toUser: string;
      fromUser: string;
      message: string;
    };
    shippingAddress: any;
    deliveryDate: {
      date: string;
      time: string;
    };
    additionalInfo: any;
    recipientInfo: any;
    userDetails: any;
    country_payment_method: any;
    promo_code_usage: any;
    order_bundle_items?: OrderBundleItemForPDF[];
  };
  orderItems: Array<{
    type: 'cake' | 'addon';
    index: number;
    qty: string;
    unitPrice: string;
    cake?: any;
    addon?: any;
  }>;
}

const getBundleLineTotal = (item: OrderBundleItemForPDF) =>
  Number(item.subTotal) || Number(item.qty) * Number(item.unitPrice);

/** Kept for call-site compatibility — package images are not shown in the PDF. */
export const prepareOrderBundleImagesForPDF = async (
  orderDetails: OrderDetailsForPDF,
  _assetUrl: string
): Promise<OrderDetailsForPDF> => orderDetails;

const formatBundleLabel = (labelEn: string, labelAr?: string) =>
  `${labelEn}${labelAr ? ` (${labelAr})` : ''}`;

const getCakeAdditionalGuide = (cake?: { instruction?: unknown }) =>
  getOrderCakeInstructionForRow({
    order_cake: { instruction: cake?.instruction },
  } as any);


const renderPackageLineRow = (
  label: string,
  name: string,
  priceText: string,
  options?: { isTotal?: boolean }
) => `
  <div class="package-line-row${options?.isTotal ? ' package-line-total' : ''}">
    <span class="package-line-label">${label}</span>
    <span class="package-line-name">${name}</span>
    <span class="package-line-price">${priceText}</span>
  </div>
`;

const renderBundleExtrasSection = (
  bundleItems: OrderBundleItemForPDF[] = [],
  options?: { title?: string; totalLabel?: string; assetUrl?: string }
): string => {
  if (!bundleItems.length) {
    return '';
  }

  const sectionTitle = options?.title ?? 'Packages:';
  const totalLabel = options?.totalLabel ?? 'Packages Total:';
  void options?.assetUrl;

  const bundleBlocks = bundleItems
    .map((bundleItem, index) => {
      const bundleTotal = getBundleLineTotal(bundleItem);

      const headerRows = [
        renderPackageLineRow(
          'Product',
          formatBundleLabel(bundleItem.labelEn, bundleItem.labelAr),
          ''
        ),
        renderPackageLineRow('Qty', String(bundleItem.qty || 1), ''),
      ].join('');

      const componentRows = (bundleItem.components || [])
        .map((component, componentIndex) => {
          const label =
            (bundleItem.components || []).length > 1
              ? `Component ${componentIndex + 1}`
              : 'Component';
          const name = `${formatBundleLabel(
            component.labelEn,
            component.labelAr
          )}${component.qty ? ` x ${component.qty}` : ''}`;
          const priceText = `${Number(
            component.subTotal ?? component.unitPrice ?? 0
          ).toFixed(2)} QAR`;
          return renderPackageLineRow(label, name, priceText);
        })
        .join('');

      const variantRows = (bundleItem.variantSelections || [])
        .map((selection, variantIndex) => {
          const label =
            (bundleItem.variantSelections || []).length > 1
              ? `Variant ${variantIndex + 1}`
              : 'Variant';
          const name = formatBundleLabel(selection.labelEn, selection.labelAr);
          const priceText = `${Number(
            selection.subTotal ?? selection.price ?? 0
          ).toFixed(2)} QAR`;
          return renderPackageLineRow(label, name, priceText);
        })
        .join('');

      return `
        <div class="bundle-product-block avoid-break">
          ${
            bundleItems.length > 1
              ? `<div class="cake-header" style="font-size: 12px; margin-bottom: 10px;">Package ${index + 1}:</div>`
              : ''
          }
          <div class="package-lines">
            ${headerRows}
            ${componentRows}
            ${variantRows}
            ${renderPackageLineRow('', 'Price', `${bundleTotal.toFixed(2)} QAR`, {
              isTotal: true,
            })}
          </div>
        </div>
      `;
    })
    .join('');

  const extrasTotal = bundleItems.reduce(
    (sum, item) => sum + getBundleLineTotal(item),
    0
  );

  return `
    <div class="bundle-extras-section">
      <div class="cake-details-title">${sectionTitle}</div>
      ${bundleBlocks}
      <div class="package-total-grid">
        <span class="package-line-label"></span>
        <span class="package-line-name">${totalLabel}</span>
        <span class="package-line-price">${extrasTotal.toFixed(2)} QAR</span>
      </div>
    </div>
  `;
};

const renderBundleOnlySection = (
  bundleItems: OrderBundleItemForPDF[],
  assetUrl: string
): string => {
  if (!bundleItems.length) {
    return '';
  }

  return `
    <div class="section-title">PACKAGES</div>
    <div style="margin-bottom: 30px;" class="avoid-break">
      ${renderBundleExtrasSection(bundleItems, {
        title: 'Packages:',
        totalLabel: 'Packages Total:',
        assetUrl,
      })}
    </div>
  `;
};

const resolvePrintImageSrc = (
  raw: string | null | undefined,
  assetUrl: string
): string | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const url = parsed?.printImageUrl;
    if (!url || typeof url !== 'string') return null;
    return url.startsWith('http') || url.startsWith('data:')
      ? url
      : `${assetUrl}/${url}`;
  } catch {
    if (raw.trim().startsWith('{')) return null;
    return raw.startsWith('http') || raw.startsWith('data:')
      ? raw
      : `${assetUrl}/${raw}`;
  }
};

const getPdfCakeBox = (cake?: any) =>
  cake?.cakeBox || cake?.cake_box || null;

const renderCakeDetailsPricingRows = (
  cakeItem: OrderDetailsForPDF['orderItems'][0],
  bundleExtrasTotal = 0,
  cakeBox?: { labelEn?: string; price?: number } | null
): string => {
  const qty = Number(cakeItem.qty || 1);
  const cakePrice = Number(cakeItem.unitPrice || 0) * qty;
  const cakeBoxPrice = cakeBox ? Number(cakeBox.price || 0) * qty : 0;
  const extrasTotal = Number(bundleExtrasTotal || 0) + cakeBoxPrice;

  return `
          <div class="info-row">
            <span class="info-label">Cake Price:</span>
            <span class="info-value">${cakePrice.toFixed(2)} QAR</span>
          </div>
          ${
            Number(bundleExtrasTotal || 0) > 0
              ? `<div class="info-row">
            <span class="info-label">Package Price:</span>
            <span class="info-value">${Number(bundleExtrasTotal).toFixed(2)} QAR</span>
          </div>`
              : ''
          }
          ${
            extrasTotal > 0
              ? `<div class="info-row">
            <span class="info-label">Total:</span>
            <span class="info-value">${(cakePrice + extrasTotal).toFixed(
              2
            )} QAR</span>
          </div>`
              : ''
          }
  `;
};

const getPdfColorLabel = (
  colorCode: string | null | undefined,
  colorLabelByCode: Map<string, string>
) => getColorDisplayLabel(colorCode, colorLabelByCode, 'N/A');

const renderFirstCakeSection = (
  cakeItem: OrderDetailsForPDF['orderItems'][0],
  mergedImage: string | undefined,
  order: OrderDetailsForPDF['order'],
  bundleItems: OrderBundleItemForPDF[],
  bundleExtrasTotal: number,
  _assetUrl: string,
  colorLabelByCode: Map<string, string>
): string => {
  if (!cakeItem?.cake) {
    return '';
  }

  return `
    <div class="section-title">CAKE DETAILS</div>
    <div class="cake-block">
      <div class="cake-header">Cake ${cakeItem.index}:</div>

      ${
        mergedImage
          ? `
        <div class="cake-image-container">
          <img src="${mergedImage}" alt="Cake ${cakeItem.index}" />
        </div>
      `
          : `
        <div class="cake-image-container">
          <span style="color: #666; font-style: italic;">Large Image: [Image not available]</span>
        </div>
      `
      }

      <div class="cake-details-title">Cake Details:</div>
      <div class="cake-details-grid">
        <div class="cake-details-left">
          <div class="info-row">
            <span class="info-label">Shape:</span>
            <span class="info-value">${cakeItem.cake?.shape?.labelEn || 'N/A'}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Color:</span>
            <span class="info-value">${getPdfColorLabel(cakeItem.cake?.colorCode, colorLabelByCode)}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Flavor:</span>
            <span class="info-value">${cakeItem.cake?.flavor?.labelEn || 'N/A'}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Additional Guide:</span>
            <span class="info-value">${getCakeAdditionalGuide(cakeItem.cake)}</span>
          </div>
          ${
            getPdfCakeBox(cakeItem.cake)
              ? `<div class="info-row premium-packaging-row">
            <span class="info-label">Premium Packaging:</span>
            <span class="info-value">${getPdfCakeBox(cakeItem.cake).labelEn} (${Number(getPdfCakeBox(cakeItem.cake).price || 0).toFixed(2)} QAR)</span>
          </div>`
              : ''
          }
        </div>
        <div class="cake-details-right">
          <div class="info-row">
            <span class="info-label">Quantity:</span>
            <span class="info-value">${cakeItem.qty || '1'}</span>
          </div>
          <div class="info-row write-on-cake-row">
            <span class="info-label write-on-cake-label">Write on Cake Board:</span>
            <span class="info-value write-on-cake-value">${
              cakeItem.cake?.writeOnCake || 'N/A'
            }</span>
          </div>
          <div class="info-row">
            <span class="info-label">Toppings:</span>
            <span class="info-value">${
              cakeItem.cake?.topping_category_item?.labelEn || 'N/A'
            }</span>
          </div>
          ${renderCakeDetailsPricingRows(
            cakeItem,
            bundleExtrasTotal,
            getPdfCakeBox(cakeItem.cake)
          )}
        </div>
      </div>
    </div>
  `;
};

export const generateOrderDetailsHTML = (
  orderDetails: OrderDetailsForPDF,
  assetUrl: string,
  mergedImages: string[] = [],
  paletteColors: ColorLabelSource[] = []
): string => {
  const colorLabelByCode = buildColorLabelByCode(paletteColors);
  const { order, orderItems } = orderDetails;
  const bundleItems = order.order_bundle_items || [];
  const bundleExtrasTotal = bundleItems.reduce(
    (sum, item) => sum + getBundleLineTotal(item),
    0
  );
  const hasBundleExtras = bundleItems.length > 0;
  const hasCardMessage = Boolean(
    order.cardMessage &&
      (order.cardMessage.toUser ||
        order.cardMessage.message ||
        order.cardMessage.fromUser)
  );
  
  // Separate cake and addon items
  const cakeItems = orderItems.filter(item => item.type === 'cake');
  const addonItems = orderItems.filter(item => item.type === 'addon');
  
  // Debug: Log print image URLs
  console.log('Print Image URLs Debug:');
  cakeItems.forEach((cake, index) => {
    console.log(`Cake ${index + 1}:`, {
      printImageUrl: cake.cake?.printImageUrl,
      fullUrl: cake.cake?.printImageUrl ? `${assetUrl}/${cake.cake.printImageUrl}` : 'No URL'
    });
  });

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Order Details - #${order.orderNumber}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Helvetica', 'Arial', sans-serif;
          font-size: 10px;
          line-height: 1.2;
          color: #333333;
          background: #ffffff;
          padding: 0;
          margin: 0;
          width: 595px;
        }
        
        /* Order Number Header - Centered at top (y=30) */
        .order-header {
          text-align: center;
          font-weight: bold;
          font-size: 12px;
          color: #EE3750;
          margin-bottom: 16px;
        }
        
        /* Company Title - Top left */
        .company-title {
          font-size: 24px;
          font-weight: bold;
          color: #EE3750;
          margin-bottom: 6px;
        }
        
        /* Order Title - Below company title */
        .order-title {
          font-size: 16px;
          font-weight: bold;
          color: #333333;
          margin-bottom: 16px;
        }
        
        /* Section Titles - Size 14, Bold, Primary Color */
        .section-title {
          font-size: 14px;
          font-weight: bold;
          color: #EE3750;
          margin-bottom: 15px;
        }
        
        /* Info rows with exact positioning like pdf-lib */
        .info-row {
          margin-bottom: 10px;
          font-size: 10px;
        }
        
        .info-label {
          font-weight: bold;
          color: #333333;
          display: inline-block;
          width: 145px;
        }
        
        .info-value {
          color: #333333;
          margin-left: 10px;
        }
        
        /* Card message styling - exact positioning */
        .card-message-label {
          font-weight: bold;
          color: #333333;
          display: inline-block;
          width: 80px;
        }
        
        .card-message-value {
          color: #333333;
          margin-left: 10px;
        }
        
        /* Cake section styling - exact match to pdf-lib */
        .cake-header {
          font-size: 14px;
          font-weight: bold;
          color: #EE3750;
          margin-bottom: 15px;
        }
        
        .cake-image-container {
          width: 100%;
          height: 260px;
          border: 1px solid #cccccc;
          margin: 10px 0 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #ffffff;
          box-sizing: border-box;
          overflow: hidden;
        }
        
        .cake-image-container img {
          width: auto;
          height: auto;
          max-width: 95%;
          max-height: 240px;
          object-fit: contain;
          object-position: center;
          display: block;
        }

        .cake-block {
          margin-bottom: 16px;
          width: 100%;
        }

        .bundle-extras-section {
          margin-top: 8px;
          padding-top: 0;
          border-top: none;
        }

        .bundle-product-block {
          margin-bottom: 18px;
          padding-bottom: 12px;
          border-bottom: 1px solid #eeeeee;
        }

        .bundle-product-block:last-of-type {
          border-bottom: none;
          margin-bottom: 8px;
        }

        .package-total-grid .info-label,
        .package-total-grid .info-value {
          font-weight: bold;
        }
        
        .cake-details-title {
          font-size: 12px;
          font-weight: bold;
          color: #EE3750;
          margin-bottom: 15px;
        }
        
        .cake-details-grid {
          display: flex;
          gap: 32px;
          width: 100%;
        }
        
        .cake-details-left,
        .cake-details-right {
          flex: 1 1 50%;
          min-width: 0;
          padding: 0;
          display: table;
          width: 100%;
          table-layout: fixed;
          border-collapse: collapse;
        }
        
        .cake-details-left .info-row,
        .cake-details-right .info-row {
          display: table-row;
        }
        
        .cake-details-left .info-label,
        .cake-details-right .info-label {
          display: table-cell;
          width: 46%;
          font-weight: bold;
          vertical-align: top;
          padding: 0 8px 8px 0;
          margin: 0;
          white-space: normal;
          line-height: 1.35;
        }
        
        .cake-details-left .info-value,
        .cake-details-right .info-value {
          display: table-cell;
          vertical-align: top;
          text-align: left;
          padding: 0 0 8px 0;
          margin: 0;
          word-break: normal;
          overflow-wrap: break-word;
        }

        .write-on-cake-label {
          white-space: normal;
          line-height: 1.35;
        }
        
        .write-on-cake-value {
          margin-left: 0;
          white-space: pre-wrap;
          word-break: normal;
          overflow-wrap: break-word;
          hyphens: none;
        }
        
        /* Addon styling - exact positioning */
        .addon-item {
          margin-bottom: 8px;
          font-size: 10px;
          color: #333333;
        }
        
        /* Print Image Section Styling */
        .print-image-section {
          margin-top: 20px;
          margin-bottom: 20px;
          page-break-inside: avoid;
        }
        
        .print-image-title {
          font-size: 12px;
          font-weight: bold;
          color: #EE3750;
          margin-bottom: 10px;
        }
        
        .print-image-container {
          border: 1px solid #e0e0e0;
          padding: 10px;
          text-align: center;
          background-color: #f9f9f9;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .print-image-container img {
          max-width: 200px;
          max-height: 150px;
          object-fit: contain;
          border-radius: 4px;
          display: block;
          margin: 0 auto;
        }
        
        /* Page structure — flow layout so cake stays under order info */
        .page {
          width: 595px;
          min-height: 0;
          height: auto;
          position: relative;
          padding: 28px 36px 36px;
          margin: 0;
          overflow: visible;
          box-sizing: border-box;
        }
        
        .page-header {
          text-align: center;
          font-weight: bold;
          font-size: 12px;
          color: #EE3750;
          margin-bottom: 24px;
        }
        
        .page-company-title {
          font-size: 24px;
          font-weight: bold;
          color: #EE3750;
          margin-bottom: 6px;
        }
        
        .page-order-title {
          font-size: 16px;
          font-weight: bold;
          color: #333333;
          margin-bottom: 20px;
        }
        
        .top-info-row {
          display: flex;
          gap: 40px;
          margin-bottom: 16px;
        }
        
        .order-info-section {
          flex: 1;
          min-width: 0;
          max-width: 100%;
        }

        .order-info-section .info-row {
          display: grid;
          grid-template-columns: 145px 1fr;
          column-gap: 10px;
          align-items: start;
        }

        .order-info-section .info-label {
          width: 145px;
          white-space: nowrap;
        }

        .order-info-section .info-value {
          margin-left: 0;
          min-width: 0;
          white-space: normal;
          word-break: normal;
          overflow-wrap: anywhere;
        }
        
        .addon-info-section {
          flex: 1;
          max-width: 220px;
        }
        
        .card-message-section {
          margin-bottom: 16px;
          width: 100%;
        }
        
        .first-cake-section {
          margin-top: 8px;
          width: 100%;
        }
        
        .page-break {
          page-break-before: always;
        }
        
        .avoid-break {
          page-break-inside: avoid;
        }

        .bundle-extras-section {
          margin-top: 8px;
          padding-top: 0;
          border-top: none;
        }

        .bundle-product-block {
          margin-bottom: 18px;
          padding-bottom: 12px;
          border-bottom: 1px solid #eeeeee;
        }

        .bundle-product-block:last-of-type {
          border-bottom: none;
          margin-bottom: 8px;
        }

        .package-lines {
          width: 100%;
        }

        .package-line-row,
        .package-total-grid {
          display: grid;
          grid-template-columns: 95px minmax(0, 1fr) 105px;
          column-gap: 12px;
          align-items: start;
          margin-bottom: 6px;
          font-size: 10px;
          width: 100%;
        }

        .package-line-label {
          font-weight: bold;
          color: #333333;
        }

        .package-line-name {
          color: #333333;
          word-break: break-word;
        }

        .package-line-price {
          text-align: right;
          color: #333333;
          white-space: nowrap;
        }

        .package-line-total {
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px solid #e0e0e0;
          font-weight: bold;
        }

        .package-line-total .package-line-name,
        .package-line-total .package-line-price {
          font-weight: bold;
        }

        .package-total-grid {
          margin-top: 10px;
          padding-top: 8px;
          border-top: 1px solid #e0e0e0;
          font-weight: bold;
        }
      </style>
    </head>
    <body>
      <!-- Page 1: Order Information + First Cake (continuous flow) -->
      <div class="page">
        <div class="page-company-title">ADORI CAKES</div>
        <div class="page-order-title">Order Details - #${order.orderNumber}</div>

        <div class="top-info-row">
          <div class="order-info-section">
            <div class="section-title">ORDER INFORMATION</div>
            <div class="info-row">
              <span class="info-label">Delivery Date:</span>
              <span class="info-value">${order.deliveryDate?.date ? formatDate(String(order.deliveryDate.date)) : 'TBD'}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Delivery Time:</span>
              <span class="info-value">${order.deliveryDate?.time || 'TBD'}</span>
            </div>
            ${
              getOrderSpecialRequestText(order as any) !== '-'
                ? `
            <div class="info-row">
              <span class="info-label">Special Request:</span>
              <span class="info-value">${getOrderSpecialRequestText(order as any)}</span>
            </div>
            `
                : ''
            }
          </div>

          ${
            addonItems.length > 0
              ? `
          <div class="addon-info-section">
            <div class="section-title">ADDON INFORMATION</div>
            ${addonItems
              .map(
                (item, index) => `
              <div class="addon-item">
                ${index + 1}. ${item.addon?.labelEn || 'N/A'} x ${item.qty || '1'}
              </div>
            `
              )
              .join('')}
          </div>
          `
              : ''
          }
        </div>

        ${
          hasCardMessage
            ? `
        <div class="card-message-section">
          <div class="section-title">CARD MESSAGE</div>
          ${
            order.cardMessage.toUser
              ? `
            <div class="info-row">
              <span class="card-message-label">To:</span>
              <span class="card-message-value">${order.cardMessage.toUser}</span>
            </div>
          `
              : ''
          }
          ${
            order.cardMessage.message
              ? `
            <div class="info-row">
              <span class="card-message-label">Message:</span>
              <span class="card-message-value">${order.cardMessage.message}</span>
            </div>
          `
              : ''
          }
          ${
            order.cardMessage.fromUser
              ? `
            <div class="info-row">
              <span class="card-message-label">From:</span>
              <span class="card-message-value">${order.cardMessage.fromUser}</span>
            </div>
          `
              : ''
          }
        </div>
        `
            : ''
        }

        ${
          cakeItems.length > 0
            ? `
        <div class="first-cake-section">
          ${renderFirstCakeSection(
            cakeItems[0],
            mergedImages[0],
            order,
            bundleItems,
            bundleExtrasTotal,
            assetUrl,
            colorLabelByCode
          )}
        </div>
        `
            : hasBundleExtras
              ? `
        <div class="first-cake-section">
          ${renderBundleOnlySection(bundleItems, assetUrl)}
        </div>
        `
              : ''
        }
      </div>

      ${
        cakeItems.length > 0 && hasBundleExtras
          ? `
      <div class="page page-break">
        <div class="page-header">Order Number: #${order.orderNumber}</div>
        ${renderBundleExtrasSection(bundleItems, { assetUrl })}
      </div>
      `
          : ''
      }
      
      <!-- Print Image Page for First Cake (only if print image exists) -->
      ${
        (() => {
          const printSrc =
            cakeItems.length > 0
              ? resolvePrintImageSrc(cakeItems[0].cake?.printImageUrl, assetUrl)
              : null;
          if (!printSrc) return '';
          return `
      <div class="page page-break">
        <div class="page-header">Order Number: #${order.orderNumber}</div>
        <div class="print-image-section">
          <div class="print-image-title">Print Image:</div>
          <div class="print-image-container">
            <img src="${printSrc}" alt="Print Image for Cake ${cakeItems[0].index}" 
                 onerror="this.style.display='none'; this.nextElementSibling.style.display='block';" />
            <span style="color: #666; font-style: italic; display: none;">Image failed to load</span>
          </div>
        </div>
      </div>
      `;
        })()
      }
      
      <!-- Additional cakes (one per page) -->
      ${
        cakeItems.length > 1
          ? cakeItems
              .slice(1)
              .map((item, index) => {
                const mergedImageUrl = mergedImages[index + 1] || null;
                const printSrc = resolvePrintImageSrc(
                  item.cake?.printImageUrl,
                  assetUrl
                );
                return `
          <div class="page page-break">
            <div class="page-header">
              Order Number: #${order.orderNumber}
            </div>

            <div class="section-title">CAKE DETAILS</div>
            <div class="cake-block">
              <div class="cake-header">Cake ${item.index}:</div>

              ${
                mergedImageUrl
                  ? `
                <div class="cake-image-container">
                  <img src="${mergedImageUrl}" alt="Cake ${item.index}" />
                </div>
              `
                  : `
                <div class="cake-image-container">
                  <span style="color: #666; font-style: italic;">Large Image: [Image not available]</span>
                </div>
              `
              }

              <div class="cake-details-title">Cake Details:</div>
              <div class="cake-details-grid">
                <div class="cake-details-left">
                  <div class="info-row">
                    <span class="info-label">Shape:</span>
                    <span class="info-value">${item.cake?.shape?.labelEn || 'N/A'}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Color:</span>
                    <span class="info-value">${getPdfColorLabel(item.cake?.colorCode, colorLabelByCode)}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Flavor:</span>
                    <span class="info-value">${item.cake?.flavor?.labelEn || 'N/A'}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Additional Guide:</span>
                    <span class="info-value">${getCakeAdditionalGuide(item.cake)}</span>
                  </div>
                  ${
                    getPdfCakeBox(item.cake)
                      ? `<div class="info-row premium-packaging-row">
                    <span class="info-label">Premium Packaging:</span>
                    <span class="info-value">${getPdfCakeBox(item.cake).labelEn} (${Number(getPdfCakeBox(item.cake).price || 0).toFixed(2)} QAR)</span>
                  </div>`
                      : ''
                  }
                </div>
                <div class="cake-details-right">
                  <div class="info-row">
                    <span class="info-label">Quantity:</span>
                    <span class="info-value">${item.qty || '1'}</span>
                  </div>
                  <div class="info-row write-on-cake-row">
                    <span class="info-label write-on-cake-label">Write on Cake Board:</span>
                    <span class="info-value write-on-cake-value">${item.cake?.writeOnCake || 'N/A'}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Toppings:</span>
                    <span class="info-value">${item.cake?.topping_category_item?.labelEn || 'N/A'}</span>
                  </div>
                  ${renderCakeDetailsPricingRows(item, 0, getPdfCakeBox(item.cake))}
                </div>
              </div>

              ${
                printSrc
                  ? `
              <div class="print-image-section">
                <div class="print-image-title">Print Image:</div>
                <div class="print-image-container">
                  <img src="${printSrc}" alt="Print Image for Cake ${item.index}" 
                       onerror="this.style.display='none'; this.nextElementSibling.style.display='block';" />
                  <span style="color: #666; font-style: italic; display: none;">Image failed to load</span>
                </div>
              </div>
              `
                  : ''
              }
            </div>
          </div>
        `;
              })
              .join('')
          : ''
      }
    </body>
    </html>
  `;
};